import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentPayments.css";

const EMPTY_PAYMENT_FORM = {
  client_id: "",
  project_id: "",
  invoice_id: "",
  payment_type: "deposit",
  amount: "",
  payment_date: new Date()
    .toISOString()
    .slice(0, 10),
  payment_method: "check",
  reference_number: "",
  notes: "",
  confirmed: true,
};

const PAYMENT_TYPES = [
  "deposit",
  "progress",
  "final",
  "refund",
  "other",
];

const PAYMENT_METHODS = [
  "check",
  "cash",
  "card",
  "stripe",
  "paypal",
  "zelle",
  "bank_transfer",
  "other",
];

function formatCurrency(value) {
  const amount = Number(value);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(
    Number.isFinite(amount)
      ? amount
      : 0
  );
}

function normalizeDate(value) {
  return value
    ? String(value).slice(0, 10)
    : "";
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(
    `${normalizeDate(value)}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function normalizePayment(payment = {}) {
  return {
    ...EMPTY_PAYMENT_FORM,
    ...payment,

    client_id:
      payment.client_id == null
        ? ""
        : String(payment.client_id),

    project_id:
      payment.project_id == null
        ? ""
        : String(payment.project_id),

    invoice_id:
      payment.invoice_id == null
        ? ""
        : String(payment.invoice_id),

    amount:
      payment.amount == null
        ? ""
        : String(payment.amount),

    payment_date:
      normalizeDate(
        payment.payment_date
      ),

    confirmed:
      Number(payment.confirmed) === 1,
  };
}

function DevelopmentPayments() {
  const [payments, setPayments] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [invoices, setInvoices] =
    useState([]);

  const [selectedPayment, setSelectedPayment] =
    useState(null);

  const [formData, setFormData] =
    useState(EMPTY_PAYMENT_FORM);

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [clientFilter, setClientFilter] =
    useState("");

  const [projectFilter, setProjectFilter] =
    useState("");

  const [invoiceFilter, setInvoiceFilter] =
    useState("");

  const [methodFilter, setMethodFilter] =
    useState("");

  const [confirmationFilter, setConfirmationFilter] =
    useState("all");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          paymentResponse,
          clientResponse,
          projectResponse,
          invoiceResponse,
        ] = await Promise.all([
          Api.getDevelopmentPayments(),
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentInvoices(),
        ]);

        setPayments(
          paymentResponse?.payments || []
        );

        setClients(
          clientResponse?.clients || []
        );

        setProjects(
          projectResponse?.projects || []
        );

        setInvoices(
          invoiceResponse?.invoices || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Payments could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          Number(project.id),
          project,
        ])
      ),
    [projects]
  );

  const invoiceMap = useMemo(
    () =>
      new Map(
        invoices.map((invoice) => [
          Number(invoice.id),
          invoice,
        ])
      ),
    [invoices]
  );

  const availableProjects = useMemo(() => {
    if (!formData.client_id) {
      return projects;
    }

    return projects.filter(
      (project) =>
        String(project.client_id) ===
        String(formData.client_id)
    );
  }, [
    projects,
    formData.client_id,
  ]);

  const availableInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (
        formData.client_id &&
        String(invoice.client_id) !==
          String(formData.client_id)
      ) {
        return false;
      }

      if (
        formData.project_id &&
        String(invoice.project_id) !==
          String(formData.project_id)
      ) {
        return false;
      }

      return true;
    });
  }, [
    invoices,
    formData.client_id,
    formData.project_id,
  ]);

  const filterProjects = useMemo(() => {
    if (!clientFilter) {
      return projects;
    }

    return projects.filter(
      (project) =>
        String(project.client_id) ===
        String(clientFilter)
    );
  }, [
    projects,
    clientFilter,
  ]);

  const filterInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (
        clientFilter &&
        String(invoice.client_id) !==
          String(clientFilter)
      ) {
        return false;
      }

      if (
        projectFilter &&
        String(invoice.project_id) !==
          String(projectFilter)
      ) {
        return false;
      }

      return true;
    });
  }, [
    invoices,
    clientFilter,
    projectFilter,
  ]);

  const filteredPayments = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return payments.filter((payment) => {
      if (
        clientFilter &&
        String(payment.client_id) !==
          String(clientFilter)
      ) {
        return false;
      }

      if (
        projectFilter &&
        String(payment.project_id) !==
          String(projectFilter)
      ) {
        return false;
      }

      if (
        invoiceFilter &&
        String(payment.invoice_id) !==
          String(invoiceFilter)
      ) {
        return false;
      }

      if (
        methodFilter &&
        String(payment.payment_method) !==
          String(methodFilter)
      ) {
        return false;
      }

      if (
        confirmationFilter ===
          "confirmed" &&
        Number(payment.confirmed) !== 1
      ) {
        return false;
      }

      if (
        confirmationFilter ===
          "unconfirmed" &&
        Number(payment.confirmed) === 1
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const client =
        clientMap.get(
          Number(payment.client_id)
        );

      const project =
        projectMap.get(
          Number(payment.project_id)
        );

      const invoice =
        invoiceMap.get(
          Number(payment.invoice_id)
        );

      const searchableText = [
        payment.payment_type,
        payment.payment_method,
        payment.reference_number,
        payment.notes,
        getClientName(client),
        project?.title,
        invoice?.invoice_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    payments,
    search,
    clientFilter,
    projectFilter,
    invoiceFilter,
    methodFilter,
    confirmationFilter,
    clientMap,
    projectMap,
    invoiceMap,
  ]);

  const summary = useMemo(() => {
    const confirmedPayments =
      payments.filter(
        (payment) =>
          Number(payment.confirmed) === 1
      );

    const unconfirmedPayments =
      payments.filter(
        (payment) =>
          Number(payment.confirmed) !== 1
      );

    return {
      totalCount: payments.length,

      confirmedCount:
        confirmedPayments.length,

      unconfirmedCount:
        unconfirmedPayments.length,

      confirmedAmount:
        confirmedPayments.reduce(
          (total, payment) =>
            total +
            (
              Number(payment.amount) || 0
            ),
          0
        ),

      unconfirmedAmount:
        unconfirmedPayments.reduce(
          (total, payment) =>
            total +
            (
              Number(payment.amount) || 0
            ),
          0
        ),
    };
  }, [payments]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewPaymentForm = () => {
    clearMessages();
    setSelectedPayment(null);
    setFormData(
      EMPTY_PAYMENT_FORM
    );
    setShowForm(true);
  };

  const openEditPaymentForm = (
    payment
  ) => {
    clearMessages();
    setSelectedPayment(payment);
    setFormData(
      normalizePayment(payment)
    );
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedPayment(null);
    setFormData(
      EMPTY_PAYMENT_FORM
    );
    setShowForm(false);
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setFormData((current) => {
      const next = {
        ...current,
        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (name === "client_id") {
        next.project_id = "";
        next.invoice_id = "";
      }

      if (name === "project_id") {
        next.invoice_id = "";
      }

      if (
        name === "invoice_id" &&
        value
      ) {
        const invoice =
          invoiceMap.get(
            Number(value)
          );

        if (invoice) {
          next.client_id =
            String(invoice.client_id);

          next.project_id =
            String(invoice.project_id);

          if (!current.amount) {
            next.amount =
              String(
                invoice.balance_due || ""
              );
          }

          if (
            current.payment_type ===
            "deposit"
          ) {
            next.payment_type =
              invoice.invoice_type ||
              current.payment_type;
          }
        }
      }

      return next;
    });
  };

  const validateForm = () => {
    if (!formData.client_id) {
      return "A client is required.";
    }

    if (!formData.project_id) {
      return "A project is required.";
    }

    if (!formData.payment_date) {
      return "Payment date is required.";
    }

    const amount =
      Number(formData.amount);

    if (
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      return "Payment amount must be greater than 0.";
    }

    return "";
  };

  const buildPayload = () => ({
    client_id:
      Number(formData.client_id),

    project_id:
      Number(formData.project_id),

    invoice_id:
      formData.invoice_id
        ? Number(
            formData.invoice_id
          )
        : null,

    payment_type:
      formData.payment_type,

    amount:
      Number(formData.amount),

    payment_date:
      formData.payment_date,

    payment_method:
      formData.payment_method ||
      null,

    reference_number:
      formData.reference_number.trim() ||
      null,

    notes:
      formData.notes.trim() ||
      null,

    confirmed:
      Boolean(formData.confirmed),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();
    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedPayment) {
        await Api.updateDevelopmentPayment(
          selectedPayment.id,
          payload
        );

        setSuccess(
          "Payment updated successfully."
        );
      } else {
        await Api.createDevelopmentPayment(
          payload
        );

        setSuccess(
          "Payment created successfully."
        );
      }

      await loadData();
      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The payment could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const toggleConfirmed = async (
    payment
  ) => {
    clearMessages();

    const nextConfirmed =
      Number(payment.confirmed) !== 1;

    try {
      await Api.updateDevelopmentPayment(
        payment.id,
        {
          confirmed: nextConfirmed,
        }
      );

      setSuccess(
        nextConfirmed
          ? "Payment confirmed."
          : "Payment marked unconfirmed."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The payment could not be updated."
      );
    }
  };

  const deletePayment = async (
    payment
  ) => {
    const confirmed =
      window.confirm(
        `Delete the ${formatCurrency(
          payment.amount
        )} payment dated ${formatDate(
          payment.payment_date
        )}?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentPayment(
        payment.id
      );

      setSuccess(
        "Payment deleted successfully."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The payment could not be deleted."
      );
    }
  };

  return (
    <div className="development-payments-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Payment management
          </p>

          <h2>Payments</h2>

          <p>
            Record payments, confirm receipt,
            and review payments by client,
            project, invoice, and payment method.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewPaymentForm
            }
          >
            Add payment
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <PaymentForm
          formData={formData}
          clients={clients}
          projects={
            availableProjects
          }
          invoices={
            availableInvoices
          }
          selectedPayment={
            selectedPayment
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-payment-summary-grid">
        <SummaryCard
          label="All payments"
          value={summary.totalCount}
          active={
            confirmationFilter ===
            "all"
          }
          onClick={() =>
            setConfirmationFilter(
              "all"
            )
          }
        />

        <SummaryCard
          label="Confirmed"
          value={
            summary.confirmedCount
          }
          active={
            confirmationFilter ===
            "confirmed"
          }
          onClick={() =>
            setConfirmationFilter(
              "confirmed"
            )
          }
        />

        <SummaryCard
          label="Unconfirmed"
          value={
            summary.unconfirmedCount
          }
          active={
            confirmationFilter ===
            "unconfirmed"
          }
          onClick={() =>
            setConfirmationFilter(
              "unconfirmed"
            )
          }
        />

        <SummaryCard
          label="Confirmed total"
          value={formatCurrency(
            summary.confirmedAmount
          )}
          active={false}
          onClick={() =>
            setConfirmationFilter(
              "confirmed"
            )
          }
        />

        <SummaryCard
          label="Pending total"
          value={formatCurrency(
            summary.unconfirmedAmount
          )}
          active={false}
          onClick={() =>
            setConfirmationFilter(
              "unconfirmed"
            )
          }
        />
      </section>

      <section className="development-payments-panel">
        <div className="development-payments-toolbar">
          <div>
            <h3>Payment records</h3>

            <p>
              {filteredPayments.length} payment
              {filteredPayments.length === 1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-payment-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search payments..."
              aria-label="Search payments"
            />

            <select
              value={clientFilter}
              onChange={(event) => {
                setClientFilter(
                  event.target.value
                );
                setProjectFilter("");
                setInvoiceFilter("");
              }}
            >
              <option value="">
                All clients
              </option>

              {clients.map(
                (client) => (
                  <option
                    key={client.id}
                    value={client.id}
                  >
                    {getClientName(
                      client
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={projectFilter}
              onChange={(event) => {
                setProjectFilter(
                  event.target.value
                );
                setInvoiceFilter("");
              }}
            >
              <option value="">
                All projects
              </option>

              {filterProjects.map(
                (project) => (
                  <option
                    key={project.id}
                    value={project.id}
                  >
                    {project.title}
                  </option>
                )
              )}
            </select>

            <select
              value={invoiceFilter}
              onChange={(event) =>
                setInvoiceFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All invoices
              </option>

              {filterInvoices.map(
                (invoice) => (
                  <option
                    key={invoice.id}
                    value={invoice.id}
                  >
                    {invoice.invoice_number}
                  </option>
                )
              )}
            </select>

            <select
              value={methodFilter}
              onChange={(event) =>
                setMethodFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All methods
              </option>

              {PAYMENT_METHODS.map(
                (method) => (
                  <option
                    key={method}
                    value={method}
                  >
                    {formatLabel(method)}
                  </option>
                )
              )}
            </select>

            <select
              value={
                confirmationFilter
              }
              onChange={(event) =>
                setConfirmationFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All confirmation statuses
              </option>

              <option value="confirmed">
                Confirmed
              </option>

              <option value="unconfirmed">
                Unconfirmed
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading payments...
          </div>
        ) : filteredPayments.length ===
          0 ? (
          <div className="development-admin-empty">
            No payments match the current
            filters.
          </div>
        ) : (
          <div className="development-payment-table-wrapper">
            <table className="development-payment-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Invoice</th>
                  <th>Type</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Confirmed</th>
                  <th />
                </tr>
              </thead>

              <tbody>
                {filteredPayments.map(
                  (payment) => {
                    const client =
                      clientMap.get(
                        Number(
                          payment.client_id
                        )
                      );

                    const project =
                      projectMap.get(
                        Number(
                          payment.project_id
                        )
                      );

                    const invoice =
                      invoiceMap.get(
                        Number(
                          payment.invoice_id
                        )
                      );

                    const isConfirmed =
                      Number(
                        payment.confirmed
                      ) === 1;

                    return (
                      <tr key={payment.id}>
                        <td>
                          {formatDate(
                            payment.payment_date
                          )}
                        </td>

                        <td>
                          <strong>
                            {getClientName(
                              client
                            )}
                          </strong>
                        </td>

                        <td>
                          {project?.title ||
                            "—"}
                        </td>

                        <td>
                          {invoice?.invoice_number ||
                            "—"}
                        </td>

                        <td>
                          {formatLabel(
                            payment.payment_type
                          )}
                        </td>

                        <td>
                          <div className="development-payment-method-cell">
                            <span>
                              {formatLabel(
                                payment.payment_method ||
                                  "not specified"
                              )}
                            </span>

                            {payment.reference_number && (
                              <small>
                                {
                                  payment.reference_number
                                }
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          <strong className="development-payment-amount">
                            {formatCurrency(
                              payment.amount
                            )}
                          </strong>
                        </td>

                        <td>
                          <span
                            className={`development-payment-confirmed-status ${
                              isConfirmed
                                ? "confirmed"
                                : "unconfirmed"
                            }`}
                          >
                            {isConfirmed
                              ? "Confirmed"
                              : "Unconfirmed"}
                          </span>
                        </td>

                        <td>
                          <div className="development-payment-actions">
                            <button
                              type="button"
                              onClick={() =>
                                openEditPaymentForm(
                                  payment
                                )
                              }
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className={
                                isConfirmed
                                  ? "unconfirm"
                                  : "confirm"
                              }
                              onClick={() =>
                                toggleConfirmed(
                                  payment
                                )
                              }
                            >
                              {isConfirmed
                                ? "Unconfirm"
                                : "Confirm"}
                            </button>

                            <button
                              type="button"
                              className="delete"
                              onClick={() =>
                                deletePayment(
                                  payment
                                )
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-payment-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function PaymentForm({
  formData,
  clients,
  projects,
  invoices,
  selectedPayment,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-payment-form-panel">
      <div className="development-payment-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedPayment
              ? "Edit payment"
              : "New payment"}
          </p>

          <h3>
            {selectedPayment
              ? `${formatCurrency(
                  selectedPayment.amount
                )} payment`
              : "Record payment"}
          </h3>
        </div>

        <button
          type="button"
          className="development-payment-close-button"
          onClick={onCancel}
          aria-label="Close payment form"
        >
          ×
        </button>
      </div>

      <form
        className="development-payment-form"
        onSubmit={onSubmit}
      >
        <div className="development-payment-form-grid">
          <label className="development-payment-field">
            <span>Client *</span>

            <select
              name="client_id"
              value={formData.client_id}
              onChange={onChange}
              required
            >
              <option value="">
                Select client
              </option>

              {clients.map(
                (client) => (
                  <option
                    key={client.id}
                    value={client.id}
                  >
                    {getClientName(
                      client
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-payment-field">
            <span>Project *</span>

            <select
              name="project_id"
              value={formData.project_id}
              onChange={onChange}
              required
            >
              <option value="">
                Select project
              </option>

              {projects.map(
                (project) => (
                  <option
                    key={project.id}
                    value={project.id}
                  >
                    {project.title}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-payment-field">
            <span>Invoice</span>

            <select
              name="invoice_id"
              value={formData.invoice_id}
              onChange={onChange}
            >
              <option value="">
                No linked invoice
              </option>

              {invoices.map(
                (invoice) => (
                  <option
                    key={invoice.id}
                    value={invoice.id}
                  >
                    {invoice.invoice_number} —{" "}
                    {formatCurrency(
                      invoice.balance_due
                    )}{" "}
                    due
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-payment-field">
            <span>Payment type</span>

            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={onChange}
            >
              {PAYMENT_TYPES.map(
                (paymentType) => (
                  <option
                    key={paymentType}
                    value={paymentType}
                  >
                    {formatLabel(
                      paymentType
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-payment-field">
            <span>Amount *</span>

            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={onChange}
              min="0.01"
              step="0.01"
              required
            />
          </label>

          <label className="development-payment-field">
            <span>Payment date *</span>

            <input
              type="date"
              name="payment_date"
              value={
                formData.payment_date
              }
              onChange={onChange}
              required
            />
          </label>

          <label className="development-payment-field">
            <span>Payment method</span>

            <select
              name="payment_method"
              value={
                formData.payment_method
              }
              onChange={onChange}
            >
              {PAYMENT_METHODS.map(
                (method) => (
                  <option
                    key={method}
                    value={method}
                  >
                    {formatLabel(method)}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-payment-field">
            <span>Reference number</span>

            <input
              type="text"
              name="reference_number"
              value={
                formData.reference_number
              }
              onChange={onChange}
            />
          </label>

          <label className="development-payment-field full-width">
            <span>Notes</span>

            <textarea
              name="notes"
              value={formData.notes}
              onChange={onChange}
              rows="5"
            />
          </label>
        </div>

        <label className="development-payment-checkbox">
          <input
            type="checkbox"
            name="confirmed"
            checked={
              formData.confirmed
            }
            onChange={onChange}
          />

          <span>
            Payment is confirmed
          </span>
        </label>

        <div className="development-payment-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedPayment
                ? "Save changes"
                : "Record payment"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default DevelopmentPayments;